import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Alternador, BuscaRemota, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { nomeDaEmpresa, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { useListas } from './crm-listas';
import { filtrosDaVisao, filtrosParaVisao, filtrosVazios, PREDEFINIDOS, PRESETS, STATUS, VAZIOS, type FiltrosComercial, type Preset } from './crm-filtros';
import { simboloMoeda } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';

/**
 * CRM no novo desenho, lote 3: o drawer "Filtros" do Comercial (o do clone do RD). Filtros salvos pelas visões salvas do
 * núcleo (`/visoes`, tela `pipeline.crm-comercial`: criar, aplicar, renomear e excluir), predefinidos, "só sem tarefa",
 * campos vazios, status, nome, qualificação, valor, datas, empresa, campanha, fonte e produto.
 */
export const TELA_FILTROS = 'pipeline.crm-comercial';
interface Visao {
  id: string;
  nome: string;
  filtros: Record<string, unknown>;
  meu?: boolean;
}

export function DrawerFiltros({
  atual,
  salvoAtual,
  aoAplicar,
  aoFechar,
}: {
  atual: FiltrosComercial;
  /** O filtro salvo em uso (o último escolhido): renomear e excluir valem para ele. */
  salvoAtual: string;
  /** `salvo`: o id do filtro salvo aplicado (vazio quando os filtros são outros). */
  aoAplicar: (f: FiltrosComercial, salvo: string) => void;
  aoFechar: () => void;
}) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const listas = useListas();
  const catalogo = useEmpresasDoCatalogo().data?.empresas ?? [];
  const qc = useQueryClient();
  const toast = useToast();
  const id = useId();
  const [f, setF] = useState<FiltrosComercial>(atual);
  const [salvo, setSalvo] = useState(salvoAtual);
  const [nomeNovo, setNomeNovo] = useState<{ modo: 'criar' | 'renomear'; nome: string } | null>(null);
  const [excluir, setExcluir] = useState(false);
  const visoes = useQuery({ queryKey: ['visoes', TELA_FILTROS], queryFn: () => get<Visao[]>('/visoes', { tela: TELA_FILTROS }) });
  const mudar = (d: Partial<FiltrosComercial>) => setF((x) => ({ ...x, ...d }));
  const recarregar = () => qc.invalidateQueries({ queryKey: ['visoes', TELA_FILTROS] });
  const gravar = useMutation({
    mutationFn: async () => {
      if (!nomeNovo?.nome.trim()) return;
      if (nomeNovo.modo === 'criar') {
        const novo = crypto.randomUUID();
        await post('/visoes', { id: novo, tela: TELA_FILTROS, nome: nomeNovo.nome.trim(), filtros: filtrosParaVisao(f) });
        setSalvo(novo);
      } else await patch(`/visoes/${salvo}`, { nome: nomeNovo.nome.trim() });
    },
    onSuccess: async () => {
      toast.sucesso(t('crm.filtros.salvo'));
      setNomeNovo(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const apagar = useMutation({
    mutationFn: () => del(`/visoes/${salvo}`),
    onSuccess: async () => {
      setSalvo('');
      setExcluir(false);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const presetSel = (k: 'criada' | 'contato' | 'proxima' | 'fechada' | 'previsao', rotulo: string) => (
    <Rotulo id={`${id}-${k}`} rotulo={rotulo}>
      <Selecao id={`${id}-${k}`} data-preset={k} value={f[k]} vazio={t('crm.filtros.qualquerData')} opcoes={PRESETS.map((p) => ({ valor: p, rotulo: t(`crm.filtros.presets.${p}`) }))} onChange={(e) => mudar({ [k]: e.target.value as Preset })} />
    </Rotulo>
  );
  return (
    <>
      <Drawer
        aberto
        aoFechar={aoFechar}
        titulo={t('crm.filtros.titulo')}
        rodape={
          <>
            <Botao
              variante="fantasma"
              onClick={() => {
                aoAplicar(filtrosVazios(), '');
                aoFechar();
              }}
            >
              {t('crm.filtros.limpar')}
            </Botao>
            <Botao
              variante="primario"
              data-aplicar-filtros=""
              onClick={() => {
                aoAplicar(f, '');
                aoFechar();
              }}
            >
              {t('crm.filtros.aplicar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4" data-drawer-filtros="">
          <div className="flex flex-col gap-2">
            <Rotulo id={`${id}-salvos`} rotulo={t('crm.filtros.salvos')}>
              <div className="flex items-center gap-1">
                <Selecao
                  id={`${id}-salvos`}
                  data-filtros-salvos=""
                  value={salvo}
                  vazio={t('crm.filtros.escolherSalvo')}
                  opcoes={(visoes.data ?? []).map((v) => ({ valor: v.id, rotulo: v.nome }))}
                  onChange={(e) => {
                    setSalvo(e.target.value);
                    const v = visoes.data?.find((x) => x.id === e.target.value);
                    if (v) {
                      aoAplicar(filtrosDaVisao(v.filtros), v.id);
                      toast.sucesso(t('crm.filtros.aplicado', { nome: v.nome }));
                      aoFechar();
                    }
                  }}
                />
                <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.filtros.renomear')} title={t('crm.filtros.renomear')} disabled={!salvo} icone={<Pencil className="size-4" />} onClick={() => setNomeNovo({ modo: 'renomear', nome: visoes.data?.find((x) => x.id === salvo)?.nome ?? '' })} />
                <Botao variante="fantasma" tamanho="sm" aria-label={t('crm.filtros.excluir')} title={t('crm.filtros.excluir')} disabled={!salvo} icone={<Trash2 className="size-4" />} onClick={() => setExcluir(true)} />
              </div>
            </Rotulo>
            <Botao tamanho="sm" className="self-start" onClick={() => setNomeNovo({ modo: 'criar', nome: '' })} data-salvar-filtro="">
              {t('crm.filtros.salvarAtual')}
            </Botao>
          </div>
          <Rotulo id={`${id}-pre`} rotulo={t('crm.filtros.predefinidos')}>
            <Selecao id={`${id}-pre`} value={f.predefinido} vazio={t('crm.filtros.nenhum')} opcoes={PREDEFINIDOS.map((p) => ({ valor: p, rotulo: t(`crm.filtros.predef.${p}`) }))} onChange={(e) => mudar({ predefinido: e.target.value as FiltrosComercial['predefinido'] })} />
          </Rotulo>
          <label className="flex items-center gap-3 text-sm font-medium">
            <Alternador marcado={f.sem_tarefa} aoMudar={(v) => mudar({ sem_tarefa: v })} rotulo={t('crm.filtros.semTarefa')} />
            {t('crm.filtros.semTarefa')}
          </label>
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('crm.filtros.vazios')}</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {VAZIOS.map((v) => (
                <label key={v} className="flex min-h-8 items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.vazios.includes(v)} onChange={(e) => mudar({ vazios: e.target.checked ? [...f.vazios, v] : f.vazios.filter((x) => x !== v) })} />
                  {t(`crm.filtros.campoVazio.${v}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <Rotulo id={`${id}-status`} rotulo={t('crm.comercial.status')}>
            <Selecao id={`${id}-status`} value={f.status} opcoes={STATUS.map((s) => ({ valor: s, rotulo: t(`crm.comercial.statusFiltro.${s}`) }))} onChange={(e) => mudar({ status: e.target.value as FiltrosComercial['status'] })} />
          </Rotulo>
          <Rotulo id={`${id}-nome`} rotulo={t('crm.filtros.nome')}>
            <Entrada id={`${id}-nome`} data-filtro-nome="" value={f.nome} onChange={(e) => mudar({ nome: e.target.value })} />
          </Rotulo>
          <Rotulo id={`${id}-qualif`} rotulo={t('crm.comercial.qualificacao')}>
            <Selecao id={`${id}-qualif`} value={f.qualificacao} vazio={t('crm.filtros.nenhum')} opcoes={[1, 2, 3, 4, 5].map((n) => ({ valor: String(n), rotulo: t('crm.comercial.estrelas', { count: n }) }))} onChange={(e) => mudar({ qualificacao: e.target.value })} />
          </Rotulo>
          <div className="grid grid-cols-2 gap-3">
            <Rotulo id={`${id}-vmin`} rotulo={t('crm.filtros.valorMin', { simbolo: simboloMoeda(s.workspace.moeda) })}>
              <Entrada id={`${id}-vmin`} inputMode="decimal" value={f.valor_min} onChange={(e) => mudar({ valor_min: e.target.value })} />
            </Rotulo>
            <Rotulo id={`${id}-vmax`} rotulo={t('crm.filtros.valorMax', { simbolo: simboloMoeda(s.workspace.moeda) })}>
              <Entrada id={`${id}-vmax`} inputMode="decimal" value={f.valor_max} onChange={(e) => mudar({ valor_max: e.target.value })} />
            </Rotulo>
          </div>
          {presetSel('criada', t('crm.filtros.datas.criada'))}
          {presetSel('contato', t('crm.filtros.datas.contato'))}
          {presetSel('proxima', t('crm.filtros.datas.proxima'))}
          {presetSel('fechada', t('crm.filtros.datas.fechada'))}
          {presetSel('previsao', t('crm.filtros.datas.previsao'))}
          <Rotulo id={`${id}-empresa`} rotulo={t('crm.filtros.empresa')}>
            <BuscaRemota id={`${id}-empresa`} def={{ caminho: '/cadastros/parceiros', rotulo: (i) => String(i.nome) }} valor={f.parceiro_id || null} rotuloAtual={f.parceiro_nome || undefined} aoMudar={(v, item) => mudar({ parceiro_id: v ?? '', parceiro_nome: item ? String(item.nome) : '' })} />
          </Rotulo>
          <Rotulo id={`${id}-campanha`} rotulo={t('crm.filtros.campanha')}>
            <Entrada id={`${id}-campanha`} list={`${id}-campanha-opcoes`} value={f.campanha} onChange={(e) => mudar({ campanha: e.target.value })} />
            <datalist id={`${id}-campanha-opcoes`}>
              {listas.data?.campanhas.map((x) => (
                <option key={x.nome} value={x.nome} />
              ))}
            </datalist>
          </Rotulo>
          <Rotulo id={`${id}-fonte`} rotulo={t('crm.filtros.fonte')}>
            <Entrada id={`${id}-fonte`} list={`${id}-fonte-opcoes`} value={f.fonte} onChange={(e) => mudar({ fonte: e.target.value })} />
            <datalist id={`${id}-fonte-opcoes`}>
              {listas.data?.fontes.map((x) => (
                <option key={x.nome} value={x.nome} />
              ))}
            </datalist>
          </Rotulo>
          <Rotulo id={`${id}-produto`} rotulo={t('crm.filtros.produto')}>
            <BuscaRemota id={`${id}-produto`} def={{ caminho: '/cadastros/servicos', rotulo: (i) => String(i.nome) }} valor={f.servico_id || null} rotuloAtual={f.servico_nome || undefined} aoMudar={(v, item) => mudar({ servico_id: v ?? '', servico_nome: item ? String(item.nome) : '' })} />
          </Rotulo>
          {/* Review rcrm3 spec (M1, decisão 14): a vertical, só com mais de uma empresa no catálogo. */}
          {catalogo.length > 1 && (
            <Rotulo id={`${id}-vertical`} rotulo={t('crm.filtros.vertical')}>
              <Selecao id={`${id}-vertical`} data-filtro-vertical="" value={f.vertical} vazio={t('crm.filtros.nenhum')} opcoes={catalogo.map((e) => ({ valor: e.id, rotulo: nomeDaEmpresa(e) }))} onChange={(e) => mudar({ vertical: e.target.value })} />
            </Rotulo>
          )}
        </div>
      </Drawer>
      <Modal
        aberto={!!nomeNovo}
        aoFechar={() => setNomeNovo(null)}
        titulo={nomeNovo?.modo === 'renomear' ? t('crm.filtros.renomear') : t('crm.filtros.salvarAtual')}
        rodape={
          <Botao variante="primario" carregando={gravar.isPending} disabled={!nomeNovo?.nome.trim()} onClick={() => gravar.mutate()} data-gravar-filtro="">
            {t('crm.filtros.gravar')}
          </Botao>
        }
      >
        <Rotulo id={`${id}-nomefiltro`} rotulo={t('crm.filtros.nomeFiltro')} obrigatorio>
          <Entrada id={`${id}-nomefiltro`} data-nome-filtro="" maxLength={80} value={nomeNovo?.nome ?? ''} onChange={(e) => setNomeNovo((x) => (x ? { ...x, nome: e.target.value } : x))} />
        </Rotulo>
      </Modal>
      <Confirmar
        aberto={excluir}
        aoFechar={() => setExcluir(false)}
        perigoso
        carregando={apagar.isPending}
        titulo={t('crm.filtros.excluir')}
        texto={t('crm.filtros.confirmarExcluir', { nome: visoes.data?.find((x) => x.id === salvo)?.nome ?? '' })}
        aoConfirmar={() => apagar.mutate()}
      />
    </>
  );
}
